import {
	HotelsPlace,
	HotelsPlaceTypeEnum,
	HotelsQueryIncludedEntityTypesEnum,
} from '@/app/types/skyscanner-wrapper-api'
import { SearchItem, SearchItemLocationType } from '@/app/types'

/**
 * Maps Skyscanner HotelsPlaceTypeEnum to SearchItemLocationType
 */
const mapPlaceTypeToLocationType = (
	type?: HotelsPlaceTypeEnum
): SearchItemLocationType => {
	switch (type) {
		case HotelsPlaceTypeEnum.PLACE_TYPE_AIRPORT:
			return SearchItemLocationType.AIRPORT
		case HotelsPlaceTypeEnum.PLACE_TYPE_CITY:
			return SearchItemLocationType.CITY
		case HotelsPlaceTypeEnum.PLACE_TYPE_COUNTRY:
			return SearchItemLocationType.COUNTRY
		case HotelsPlaceTypeEnum.PLACE_TYPE_HOTEL:
			return SearchItemLocationType.HOTEL
		case HotelsPlaceTypeEnum.PLACE_TYPE_DISTRICT:
		case HotelsPlaceTypeEnum.PLACE_TYPE_FIRST_LEVEL_NATION_ADMINISTRATIVE_DIVISION:
		case HotelsPlaceTypeEnum.PLACE_TYPE_SECOND_LEVEL_NATION_ADMINISTRATIVE_DIVISION:
			return SearchItemLocationType.REGION
		case HotelsPlaceTypeEnum.PLACE_TYPE_ISLAND:
		case HotelsPlaceTypeEnum.PLACE_TYPE_SEA_COAST:
		case HotelsPlaceTypeEnum.PLACE_TYPE_MOUNTAIN_RANGE:
		case HotelsPlaceTypeEnum.PLACE_TYPE_SEA:
			return SearchItemLocationType.LM
		default:
			return SearchItemLocationType.CITY
	}
}

/**
 * Extracts country code country name string
 * Unreliable TODO: Check if we need cc at all
 * @example "France" -> "FR"
 */
const extractCountryCode = (country: string = ''): string => country.substring(0, 2).toUpperCase()

/**
 * Extracts city name from hierarchy string or location name
 * @example "Paris|Île-de-France|France" -> "Paris"
 */
const extractCityName = (
	hierarchyParts: string[],
	hotelsPlace: HotelsPlace
): string => {
	switch (hotelsPlace.type) {
		case HotelsPlaceTypeEnum.PLACE_TYPE_AIRPORT:
		case HotelsPlaceTypeEnum.PLACE_TYPE_HOTEL:
		case HotelsPlaceTypeEnum.PLACE_TYPE_DISTRICT:
			return hierarchyParts.length ? hierarchyParts[0] : ''
		case HotelsPlaceTypeEnum.PLACE_TYPE_CITY:
			return hotelsPlace.name || ''
		case HotelsPlaceTypeEnum.PLACE_TYPE_UNSPECIFIED:
		case HotelsPlaceTypeEnum.PLACE_TYPE_COUNTRY:
		case HotelsPlaceTypeEnum.PLACE_TYPE_ISLAND:
		case HotelsPlaceTypeEnum.PLACE_TYPE_FIRST_LEVEL_NATION_ADMINISTRATIVE_DIVISION:
		case HotelsPlaceTypeEnum.PLACE_TYPE_SECOND_LEVEL_NATION_ADMINISTRATIVE_DIVISION:
		case HotelsPlaceTypeEnum.PLACE_TYPE_SEA_COAST:
		case HotelsPlaceTypeEnum.PLACE_TYPE_MOUNTAIN_RANGE:
		case HotelsPlaceTypeEnum.PLACE_TYPE_SEA:
		default:
			return ''
	}
}

/**
 * Extracts region name from hierarchy string or location name
 * @example "Paris|Île-de-France|France" -> "Île-de-France"
 */
const extractRegion = (
	hierarchyParts: string[],
	hotelsPlace: HotelsPlace
): string => {
	switch (hotelsPlace.type) {
		case HotelsPlaceTypeEnum.PLACE_TYPE_DISTRICT:
		case HotelsPlaceTypeEnum.PLACE_TYPE_FIRST_LEVEL_NATION_ADMINISTRATIVE_DIVISION:
		case HotelsPlaceTypeEnum.PLACE_TYPE_SECOND_LEVEL_NATION_ADMINISTRATIVE_DIVISION:
			return hotelsPlace.name || ''
		default:
			return hierarchyParts.length > 1 ? hierarchyParts[hierarchyParts.length - 2] : ''
	}
}

/**
 * Extracts country name from hierarchy string or location name
 * @example "Paris|Île-de-France|France" -> "France"
 */
const extractCountry = (
	hierarchyParts: string[],
	hotelsPlace: HotelsPlace
): string => {
	if (hotelsPlace.type === HotelsPlaceTypeEnum.PLACE_TYPE_COUNTRY) {
		return hotelsPlace.name || '';
	}
	return hierarchyParts.length ? hierarchyParts[hierarchyParts.length - 1] : '';
}

/**
 * Parses location string to extract latitude and longitude
 * @example "48.8566, 2.3522" -> { lat: 48.8566, lng: 2.3522 }
 */
const parseLocation = (
	location?: string
): { lat: number; lng: number } | { lat: number; lng: number } => {
	if (!location) return { lat: 0, lng: 0 }

	const [latStr, lngStr] = location.split(',')
	const lat = parseFloat(latStr?.trim() || '0')
	const lng = parseFloat(lngStr?.trim() || '0')

	return { lat, lng }
}

/**
 * Removes HTML tags from highlighted text
 * @example "<em>Paris</em>" -> "Paris"
 */
const stripHtmlTags = (text?: string): string => {
	if (!text) return ''
	return text.replace(/<[^>]*>/g, '')
}

/**
 * Transforms HotelsPlace from Skyscanner API to SearchItem format
 *
 * @param hotelsPlace - The HotelsPlace object from Skyscanner API
 * @returns SearchItem object compatible with the application's search interface
 *
 * @example
 * ```typescript
 * const hotelsPlace: HotelsPlace = {
 *   entityId: "27539733",
 *   name: "Paris",
 *   hierarchy: "Paris|Île-de-France|France",
 *   location: "48.8566, 2.3522",
 *   type: HotelsPlaceTypeEnum.PLACE_TYPE_CITY,
 *   score: 0.95
 * };
 *
 * const searchItem = transformHotelsPlaceToSearchItem(hotelsPlace);
 * ```
 */
export const transformHotelsPlaceToSearchItem = (
	hotelsPlace: HotelsPlace
): SearchItem => {
	const hierarchyParts = hotelsPlace.hierarchy?.split('|') || []
	const { lat, lng } = parseLocation(hotelsPlace.location)
	const loctype = mapPlaceTypeToLocationType(hotelsPlace.type)
	const cityName = extractCityName(hierarchyParts, hotelsPlace)
	const region = extractRegion(hierarchyParts, hotelsPlace)
	const country = extractCountry(hierarchyParts, hotelsPlace) 
	const cc = extractCountryCode(country)

	// Build display name based on type
	let displayname =
		loctype === SearchItemLocationType.AIRPORT
			? `${hotelsPlace.name}, ${cityName}, ${country}`
			: [hotelsPlace.name, ...(country ? [country] : [])].join(', ')

	const name = hotelsPlace.name || ''

	return {
		id: hotelsPlace.entityId || '',
		displayname,
		loctype,
		country,
		cc,
		rc: '',
		hotelname: loctype === SearchItemLocationType.HOTEL ? name : '',
		cityname: displayname,
		timezone: null, // Not provided by Skyscanner API
		utc: '+00:00', // Default UTC offset, not provided by API
		airportname: loctype === SearchItemLocationType.AIRPORT ? name : '',
		ap: loctype === SearchItemLocationType.AIRPORT ? cc : '',
		apicode: hotelsPlace.entityId || '',
		cityonly: cityName,
		ctid: hotelsPlace.entityId || '',
		displayType: {
			type: loctype,
			displayName: getDisplayNameForType(loctype),
		},
		indexId: `${loctype}-${hotelsPlace.entityId}`,
		isMetroOnly: false,
		kayakId: hotelsPlace.entityId || '',
		kayakType: loctype,
		locationname: displayname,
		name,
		placeID: hotelsPlace.entityId || '',
		ptid: `${loctype.toUpperCase()}${hotelsPlace.entityId}`,
		region,
		searchFormPrimary: name,
		searchFormSecondary: displayname,
		secondary: displayname,
		shortdisplayname: name,
		smartyDisplay: displayname,
		subregion: cityName,
		entityKey: `skyscanner:${hotelsPlace.entityId}`,
		lat,
		lng,
		utcOffsetMinutes: 0, // Not provided by Skyscanner API
	}
}

/**
 * Gets human-readable display name for location type
 */
const getDisplayNameForType = (loctype: SearchItemLocationType): string => {
	switch (loctype) {
		case SearchItemLocationType.AIRPORT:
			return 'Airport'
		case SearchItemLocationType.CITY:
			return 'City'
		case SearchItemLocationType.COUNTRY:
			return 'Country'
		case SearchItemLocationType.HOTEL:
			return 'Hotel'
		case SearchItemLocationType.REGION:
			return 'Region'
		case SearchItemLocationType.LM:
			return 'Landmark'
		case SearchItemLocationType.HOOD:
			return 'Neighborhood'
		default:
			return 'Location'
	}
}

/**
 * Transforms an array of HotelsPlace objects to SearchItem array
 *
 * @param hotelsPlaces - Array of HotelsPlace objects from Skyscanner API
 * @returns Array of SearchItem objects
 *
 * @example
 * ```typescript
 * const places: HotelsPlace[] = [...];
 * const searchItems = transformHotelsPlacesToSearchItems(places);
 * ```
 */
export const transformHotelsPlacesToSearchItems = (
	hotelsPlaces: HotelsPlace[]
): SearchItem[] => {
	return hotelsPlaces.map(transformHotelsPlaceToSearchItem)
}
